<script setup lang="ts">
/**
 * Which arrangement the front page uses.
 *
 * Thumbnails, not a list of names. "Sidebar" and "Band" mean nothing until you
 * have seen them, and this is a visual decision, so the picker shows the
 * shape rather than describing it. Each thumbnail is drawn in the document's
 * own accent, which makes the choice about this report rather than about a
 * generic specimen.
 *
 * The drawings are deliberately crude: three grey bars standing for the title
 * block. A thumbnail that tried to reproduce the page would be unreadable at
 * this size and would go out of date the moment the real cover changed.
 */
import { useI18n } from 'vue-i18n'

import { COVER_VARIANT_IDS, type CoverVariant } from '@/sections/cover/variants'

const { t } = useI18n()

defineProps<{
  chosen: CoverVariant
}>()

const emit = defineEmits<{
  choose: [variant: CoverVariant]
}>()
</script>

<template>
  <div class="variants">
    <span class="variants__label">{{ t('inspector.cover.arrangement') }}</span>

    <div class="variants__grid" role="radiogroup" :aria-label="t('inspector.cover.arrangement')">
      <button
        v-for="variant in COVER_VARIANT_IDS"
        :key="variant"
        type="button"
        role="radio"
        class="variant"
        :class="{ 'variant--on': variant === chosen }"
        :aria-checked="variant === chosen"
        :title="t(`inspector.cover.variants.${variant}`)"
        @click="emit('choose', variant)"
      >
        <span class="sheet" :class="`sheet--${variant}`" aria-hidden="true">
          <span class="sheet__wash" />
          <span class="sheet__mark" />
          <span class="sheet__title" />
          <span class="sheet__line" />
          <span class="sheet__line sheet__line--short" />
        </span>
        <span class="variant__name">{{ t(`inspector.cover.variants.${variant}`) }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.variants {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}

.variants__label {
  color: var(--ink-faint);
  font-size: var(--text-xs);
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.variants__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--sp-3);
}

.variant {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-2);
  padding: var(--sp-3) var(--sp-2) var(--sp-2);
  border: 0;
  border-radius: var(--radius);
  background: none;
  color: var(--ink-faint);
  font: inherit;
  font-size: 10px;
  cursor: pointer;
  transition:
    background var(--quick),
    color var(--quick);
}

.variant:hover {
  background: var(--panel-hover);
  color: var(--ink-soft);
}

.variant--on {
  background: var(--accent-quiet);
  color: var(--accent);
}

.variant__name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* ---------- The little page ---------- */

.sheet {
  position: relative;
  display: block;
  width: 100%;
  /* A page's proportions, so a thumbnail reads as a sheet of paper. */
  aspect-ratio: 1 / 1.414;
  overflow: hidden;
  border-radius: 3px;
  background: #ffffff;
  box-shadow: 0 0 0 1px var(--hairline-strong);
  transition: box-shadow var(--quick);
}

.variant--on .sheet {
  box-shadow:
    0 0 0 1.5px var(--accent),
    var(--lift-1);
}

.sheet__wash,
.sheet__mark,
.sheet__title,
.sheet__line {
  position: absolute;
  display: block;
}

.sheet__wash {
  display: none;
  background: var(--accent-fill);
}

/* The logo. */
.sheet__mark {
  top: 11%;
  left: 12%;
  width: 24%;
  height: 5%;
  border-radius: 1px;
  background: #c8cfd8;
}

.sheet__title {
  left: 12%;
  right: 30%;
  top: 34%;
  height: 9%;
  border-radius: 1px;
  background: #4b5765;
}

.sheet__line {
  left: 12%;
  right: 22%;
  top: 48%;
  height: 3.5%;
  border-radius: 1px;
  background: #cfd6de;
}

.sheet__line--short {
  top: 56%;
  right: 52%;
}

/* Classic is the base above, so it needs nothing of its own. */

/* Centred. */
.sheet--centred .sheet__mark {
  left: 38%;
  width: 24%;
}

.sheet--centred .sheet__title {
  left: 22%;
  right: 22%;
  top: 42%;
}

.sheet--centred .sheet__line {
  left: 28%;
  right: 28%;
  top: 56%;
}

.sheet--centred .sheet__line--short {
  left: 38%;
  right: 38%;
  top: 64%;
}

/* Band. */
.sheet--band .sheet__wash {
  display: block;
  top: 0;
  left: 0;
  right: 0;
  height: 46%;
}

.sheet--band .sheet__mark {
  top: 9%;
  background: rgba(255, 255, 255, 0.8);
}

.sheet--band .sheet__title {
  top: 24%;
  background: #ffffff;
}

.sheet--band .sheet__line {
  top: 36%;
  background: rgba(255, 255, 255, 0.65);
}

.sheet--band .sheet__line--short {
  top: 56%;
  background: #cfd6de;
}

/* Portrait: a picture across the top. */
.sheet--portrait .sheet__wash {
  display: block;
  top: 0;
  left: 0;
  right: 0;
  height: 45%;
  background: #b9c2cc;
}

.sheet--portrait .sheet__mark {
  top: 51%;
}

.sheet--portrait .sheet__title {
  top: 61%;
}

.sheet--portrait .sheet__line {
  top: 74%;
}

.sheet--portrait .sheet__line--short {
  top: 81%;
}

/* Framed. */
.sheet--framed .sheet__wash {
  display: block;
  inset: 8%;
  background: none;
  border: 1px solid var(--accent);
}

.sheet--framed .sheet__mark {
  top: 22%;
  left: 38%;
  width: 24%;
}

.sheet--framed .sheet__title {
  left: 22%;
  right: 22%;
  top: 44%;
}

.sheet--framed .sheet__line {
  left: 30%;
  right: 30%;
  top: 58%;
}

.sheet--framed .sheet__line--short {
  left: 38%;
  right: 38%;
  top: 65%;
}

/* Full: a single picture, nothing drawn over it. */
.sheet--full .sheet__wash {
  display: block;
  inset: 0;
  background: #b9c2cc;
}

.sheet--full .sheet__mark,
.sheet--full .sheet__title,
.sheet--full .sheet__line {
  display: none;
}

/* Sidebar. */
.sheet--sidebar .sheet__wash {
  display: block;
  top: 0;
  bottom: 0;
  left: 0;
  width: 22%;
}

.sheet--sidebar .sheet__mark {
  left: 30%;
}

.sheet--sidebar .sheet__title {
  left: 30%;
}

.sheet--sidebar .sheet__line {
  left: 30%;
}

.sheet--sidebar .sheet__line--short {
  left: 30%;
  right: 50%;
}
</style>
